import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { Promotion } from "@omni/types/api/product/bff/shared/promotion.model";
import { SalesIncentive } from "@omni/types/api/product/bff/shared/salesIncentive.model";
import { Media } from "@omni/types/api/shared/bff/media.model";
import { ProductSliderProps } from "@omni/types/components/props/productSlider.props";
import { cn } from "@omni/utils/styles";
import { isEmpty } from "lodash-es";
import { MouseEventHandler } from "react";

import { Accessories } from "@/components/Cart/ShoppingCart/productCollectionBlock/cartItem/cartItemInfo/accessories/accessories.container";
import { Col } from "@/components/General/Grid/col.component";
import { Row } from "@/components/General/Grid/row.component";
import { Messages } from "@/components/General/Messages/messages.container";
import { Price } from "@/components/Price/price";
import { AddToCartModelRenditions } from "@/components/Product/ProductImage/mappedRenditions";
import { ProductImageContainer } from "@/components/Product/ProductImage/productImage.container";
import { PromotionPeriod } from "@/components/Product/PromotionPeriod/promotionPeriod.container";
import { useThunkDispatch } from "@/redux/dispatcher";
import { clear as clearMessages } from "@/redux/modules/value/apiMessages/apiMessages.module";
import { clearMessages as clearVisibleMessages } from "@/redux/modules/value/messages/messages.utils";

import { AddToCartModalFooter } from "./Partials/footer";
import { RelatedProducts } from "./Partials/relatedProducts";

type AddToCartComponentProps = {
    title: string;
    medias: null | Array<Media>;
    strikethroughPrice?: number | null;
    currentPrice: number;
    salesIncentives?: Array<SalesIncentive>;
    promotions?: Array<Promotion>;
    isBoxArticle: boolean;
    accessories: Array<Product<ProductFieldSet.ProductTile>>;
    productSliderProps?: ProductSliderProps;
    onClose?: MouseEventHandler<HTMLButtonElement>;
    onAddAccessory: (
        sku: string,
        product: Product<ProductFieldSet.ProductTile>
    ) => Promise<void>;
};

const AddToCartComponent = ({
    title,
    medias,
    currentPrice,
    strikethroughPrice,
    salesIncentives,
    promotions,
    isBoxArticle,
    accessories,
    productSliderProps,
    onClose,
    onAddAccessory
}: AddToCartComponentProps) => {
    const dispatch = useThunkDispatch();

    const onModalClose: MouseEventHandler<HTMLButtonElement> = (e) => {
        dispatch(clearMessages);
        clearVisibleMessages();
        onClose?.(e);
        document.body.classList.remove("modal-open");
    };

    const promotionPeriod = !isBoxArticle && promotions && salesIncentives && (
        <PromotionPeriod
            salesIncentives={salesIncentives}
            promotions={promotions}
            className={cn("tw:text-right tw:m-0")}
        />
    );

    return (
        <div>
            <Messages messageid={"add-to-cart-modal-messages"} />
            <div>
                <Row>
                    <Col
                        xs={6}
                        md={4}
                    >
                        <ProductImageContainer
                            title={title}
                            medias={medias}
                            isEcoChequesAccepted={false}
                            renditions={AddToCartModelRenditions}
                        />
                    </Col>
                    <Col
                        xs={6}
                        className={cn(
                            "tw:flex tw:flex-col tw:items-end tw:justify-center",
                            "tw:md:hidden"
                        )}
                    >
                        <Price
                            price={currentPrice}
                            strikeThroughPrice={strikethroughPrice}
                            size={"lg"}
                            className={cn("tw:ml-auto")}
                        >
                            {strikethroughPrice && <Price.StrikeThrough />}
                            <Price.Main />
                        </Price>
                        {promotionPeriod}
                    </Col>
                    <Col
                        xs={12}
                        md={4}
                    >
                        <h3>{title}</h3>
                    </Col>
                    <Col
                        className={cn("tw:hidden", "tw:md:block")}
                        md={4}
                    >
                        <Price
                            price={currentPrice}
                            strikeThroughPrice={strikethroughPrice}
                            size={"lg"}
                            className={cn("tw:ml-auto")}
                        >
                            {strikethroughPrice && <Price.StrikeThrough />}
                            <Price.Main />
                        </Price>
                        {promotionPeriod}
                    </Col>
                </Row>
                {!isEmpty(accessories) && (
                    <Row>
                        <Col
                            xs={12}
                            sm={{ size: 9, offset: 3 }}
                            md={{ size: 10, offset: 2 }}
                        >
                            <Accessories
                                accessories={accessories}
                                onAdd={onAddAccessory}
                            />
                        </Col>
                    </Row>
                )}
            </div>
            <AddToCartModalFooter onModalClose={onModalClose} />
            {productSliderProps && (
                <RelatedProducts productSliderProps={productSliderProps} />
            )}
        </div>
    );
};

export { AddToCartComponent };
